import React, { useEffect, useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts'
import { Users, MapPin, Globe, Loader2 } from 'lucide-react'
import { Card } from '../ui'
import useInstagramConnectionStore from '../../stores/instagramConnectionStore'

interface AccountDemographicsProps {
  customerId: string
  platformAccountId: string
  partitionKey: number
}

const COLORS = [
  '#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe',
  '#818cf8', '#7c3aed', '#5b21b6', '#4c1d95', '#312e81'
]

// Compact axis numbers: 16000 → "16K", 1200000 → "1.2M"
const compactNumber = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 })

// Convert ISO country codes to display names (built-in Intl API, falls back to the code)
const regionNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null

const countryName = (code: string): string => {
  if (!regionNames) return code
  try {
    return regionNames.of(code.toUpperCase()) || code
  } catch {
    return code
  }
}

const AccountDemographics: React.FC<AccountDemographicsProps> = ({
  customerId,
  platformAccountId,
  partitionKey
}) => {
  const { demographics, demographicsLoading, loadDemographics } = useInstagramConnectionStore()

  useEffect(() => {
    if (customerId && platformAccountId) {
      loadDemographics(customerId, platformAccountId, partitionKey)
    }
  }, [customerId, platformAccountId, partitionKey, loadDemographics])

  const ageData = useMemo(() => {
    if (!demographics?.age) return []
    return Object.entries(demographics.age.data)
      .map(([key, value]) => ({ name: key, value }))
      .sort((a, b) => {
        // Sort age ranges numerically
        const aNum = parseInt(a.name.split('-')[0]) || 0
        const bNum = parseInt(b.name.split('-')[0]) || 0
        return aNum - bNum
      })
  }, [demographics])

  const genderData = useMemo(() => {
    if (!demographics?.gender) return []
    const labels: Record<string, string> = { m: 'Male', f: 'Female', u: 'Unknown' }
    return Object.entries(demographics.gender.data)
      .map(([key, value]) => ({
        name: labels[key.toLowerCase()] || key.charAt(0).toUpperCase() + key.slice(1),
        value
      }))
      .sort((a, b) => b.value - a.value)
  }, [demographics])

  const cityData = useMemo(() => {
    if (!demographics?.city) return []
    return Object.entries(demographics.city.data)
      .map(([key, value]) => ({ name: key, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10) // Top 10 cities
  }, [demographics])

  const countryData = useMemo(() => {
    if (!demographics?.country) return []
    return Object.entries(demographics.country.data)
      .map(([key, value]) => ({ name: countryName(key), value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)
  }, [demographics])

  if (demographicsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Loading demographics...</span>
      </div>
    )
  }

  if (!demographics) {
    return (
      <div className="text-center py-8">
        <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">
          No demographics data available yet.
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Demographics are fetched daily for connected Instagram accounts.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5">
        {/* Age Distribution */}
        {ageData.length > 0 && (
          <Card variant="outlined" padding="md" className="bg-gradient-to-br from-white to-indigo-50/40 border-indigo-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-sm">
                <Users className="h-3.5 w-3.5 text-white" />
              </div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Age Distribution
              </h4>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={ageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => compactNumber.format(v)} />
                <RechartsTooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  formatter={(value: number) => [value.toLocaleString(), 'Followers']}
                />
                <Bar dataKey="value" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Gender Distribution */}
        {genderData.length > 0 && (
          <Card variant="outlined" padding="md" className="bg-gradient-to-br from-white to-purple-50/40 border-purple-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-sm">
                <Users className="h-3.5 w-3.5 text-white" />
              </div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Gender Distribution
              </h4>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={genderData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="45%"
                  innerRadius={52}
                  outerRadius={82}
                  paddingAngle={2}
                  label={({ x, y, textAnchor, percent }: any) => (
                    <text x={x} y={y} textAnchor={textAnchor} dominantBaseline="central" fontSize={12} fill="#4b5563">
                      {`${(percent * 100).toFixed(0)}%`}
                    </text>
                  )}
                  labelLine={false}
                >
                  {genderData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Legend verticalAlign="bottom" height={24} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <RechartsTooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  formatter={(value: number) => [value.toLocaleString(), 'Followers']}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5">
        {/* Top Cities */}
        {cityData.length > 0 && (
          <Card variant="outlined" padding="md" className="bg-gradient-to-br from-white to-blue-50/40 border-blue-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-sm">
                <MapPin className="h-3.5 w-3.5 text-white" />
              </div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Top Cities
              </h4>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={cityData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v: number) => compactNumber.format(v)} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 10 }}
                  width={150}
                />
                <RechartsTooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  formatter={(value: number) => [value.toLocaleString(), 'Followers']}
                />
                <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Top Countries */}
        {countryData.length > 0 && (
          <Card variant="outlined" padding="md" className="bg-gradient-to-br from-white to-emerald-50/40 border-emerald-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                <Globe className="h-3.5 w-3.5 text-white" />
              </div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Top Countries
              </h4>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={countryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v: number) => compactNumber.format(v)} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 10 }}
                  width={150}
                />
                <RechartsTooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  formatter={(value: number) => [value.toLocaleString(), 'Followers']}
                />
                <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* Last updated */}
      {demographics.age?.fetchedAt && (
        <p className="text-xs text-gray-400 text-right">
          Last updated: {new Date(demographics.age.fetchedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })}
        </p>
      )}
    </div>
  )
}

export default AccountDemographics
